import { type ReportResponseFilterQuestionOption } from './helper'
import { type DisplaySettings } from '../displaySettings'

export interface ReportTitleDisplayItem {
  fullTitle: string
  displayTitle: string
}

export const TITLE_SEPARATOR = ' - '

const splitReportTitle = (title: string): { prefix: string, suffix: string | null } => {
  const separatorIndex = title.indexOf(TITLE_SEPARATOR)

  if (separatorIndex === -1) {
    return {
      prefix: title.trim(),
      suffix: null
    }
  }

  return {
    prefix: title.slice(0, separatorIndex).trim(),
    suffix: title.slice(separatorIndex + TITLE_SEPARATOR.length).trim() || null
  }
}

const getComparisonTitles = (settings: DisplaySettings): string[] => {
  return settings.comparisons
    .map((comparison) => comparison.comparisonOption?.title ?? comparison.comparison ?? null)
    .filter((title): title is string => Boolean(title))
}

const getSliceTitles = (settings: DisplaySettings): string[] => {
  return settings.slices
    .map((slice) => slice?.attributeValue ?? slice?.attributeKey ?? null)
    .filter((title): title is string => Boolean(title))
}

export const getResponseFilterLabelPrefix = (
  settings: DisplaySettings,
  responseFilterQuestionOptions: ReportResponseFilterQuestionOption[] = []
): string | null => {
  const responseFilter = settings.responseFilter

  if (!responseFilter?.itemId) {
    return null
  }

  if (responseFilter.sectionTitle && responseFilter.questionNumberLabel) {
    return `${responseFilter.sectionTitle} - ${responseFilter.questionNumberLabel}: `
  }

  const matchedQuestion = responseFilterQuestionOptions.find((question) => question.itemId === responseFilter.itemId)
  if (matchedQuestion?.sectionTitle && matchedQuestion.questionNumberLabel) {
    return `${matchedQuestion.sectionTitle} - ${matchedQuestion.questionNumberLabel}: `
  }

  if (responseFilter.prompt) {
    return `${responseFilter.prompt}: `
  }

  return null
}

const getResponseFilterTitles = (
  settings: DisplaySettings,
  responseFilterQuestionOptions: ReportResponseFilterQuestionOption[] = []
): string[] => {
  const responseFilter = settings.responseFilter

  if (!responseFilter) {
    return []
  }

  const responseFilterLabelPrefix = getResponseFilterLabelPrefix(settings, responseFilterQuestionOptions)

  if (
    !responseFilterLabelPrefix ||
    !Array.isArray(responseFilter.answers) ||
    responseFilter.answers.length === 0
  ) {
    return []
  }

  return responseFilter.answers.map((answer) => `${responseFilterLabelPrefix}${answer}`)
}

export const buildReportTitlesFromSettings = (
  baseTitle: string | null | undefined,
  settings: DisplaySettings,
  responseFilterQuestionOptions: ReportResponseFilterQuestionOption[] = []
): string[] | null => {
  const responseFilterTitles = getResponseFilterTitles(settings, responseFilterQuestionOptions)

  if (responseFilterTitles.length > 0) {
    return baseTitle ? [baseTitle, ...responseFilterTitles] : responseFilterTitles
  }

  const comparisonTitles = getComparisonTitles(settings)
  const sliceTitles = getSliceTitles(settings)

  let derivedTitles: string[] = []

  // Keep the pending title construction rules in the same module that owns
  // report title formatting so loading chips and resolved chips stay aligned.
  if (comparisonTitles.length > 0 && sliceTitles.length > 0) {
    derivedTitles = comparisonTitles.flatMap((comparisonTitle) =>
      sliceTitles.map((sliceTitle) => `${comparisonTitle}${TITLE_SEPARATOR}${sliceTitle}`)
    )
  } else if (comparisonTitles.length > 0) {
    derivedTitles = comparisonTitles
  } else if (sliceTitles.length > 0) {
    derivedTitles = sliceTitles
  }

  if (derivedTitles.length === 0) {
    return null
  }

  return baseTitle ? [baseTitle, ...derivedTitles] : derivedTitles
}

export const getReportTitleDisplayItems = (titles: string[] = []): ReportTitleDisplayItem[] => {
  return titles.map((title) => ({
    fullTitle: title,
    displayTitle: title
  }))
}
